import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Download, Mail, Monitor, RefreshCw, ShieldCheck, Smartphone, UserRound } from 'lucide-react';

const FIXED_POSITION = 'Sales Manager';
const ENPOWER_COMPANY_ADDRESS_LINE_1 = 'Halberstädter Straße 21';
const ENPOWER_COMPANY_ADDRESS_LINE_2 = '39112 Magdeburg';

const EMPTY_PROFILE = {
  name: '',
  position: FIXED_POSITION,
  email: '',
  phone: '',
  company: 'Kirsten und Klein GbR EN:POWER',
  website: 'www.en-power.eu',
  addressLine1: ENPOWER_COMPANY_ADDRESS_LINE_1,
  addressLine2: ENPOWER_COMPANY_ADDRESS_LINE_2,
  missingFields: [],
};

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function getValueLabel(value, fallback = 'Nicht hinterlegt') {
  return String(value || '').trim() || fallback;
}

function cleanEditableValue(value, maxLength = 140) {
  return String(value || '')
    .replace(/[\u0000-\u001F\u007F]+/g, ' ')
    .replace(/\s+/g, ' ')
    .slice(0, maxLength);
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function normalizeWebsiteHref(value) {
  const website = String(value || '').trim();
  if (!website) return '#';
  return /^https?:\/\//i.test(website) ? website : `https://${website}`;
}

function buildSignatureHtml(profile, logoSrc) {
  const phone = String(profile.phone || '').trim();
  const optionalPhone = phone
    ? `<div>Mobil: ${escapeHtml(phone)}</div>`
    : '';

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>EN:POWER Signatur</title>
</head>
<body style="margin:0;padding:0;background:#ffffff;">
  <div style="font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.55;color:#333333;">
    <div>--</div>
    <br>
    <div>Mit freundlichen Gr&uuml;&szlig;en,</div>
    <br>
    <div>${escapeHtml(profile.name)}</div>
    <div>${escapeHtml(FIXED_POSITION)}</div>
    <div>${escapeHtml(profile.company)}</div>
    <br>
    <div>${escapeHtml(profile.addressLine1)}</div>
    <div>${escapeHtml(profile.addressLine2)}</div>
    ${optionalPhone}
    <div>E-Mail: <a href="mailto:${escapeHtml(profile.email)}" style="color:#F58200;text-decoration:underline;">${escapeHtml(profile.email)}</a></div>
    <div>Website: <a href="${escapeHtml(normalizeWebsiteHref(profile.website))}" style="color:#F58200;text-decoration:underline;">${escapeHtml(profile.website)}</a></div>
    <br>
    <img src="${escapeHtml(logoSrc)}" width="470" alt="EN:POWER" style="display:block;width:470px;max-width:470px;height:auto;border:0;outline:none;text-decoration:none;">
  </div>
</body>
</html>`;
}

function buildSignatureText(profile) {
  return [
    '--',
    '',
    'Mit freundlichen Grüßen,',
    '',
    profile.name,
    FIXED_POSITION,
    profile.company,
    '',
    profile.addressLine1,
    profile.addressLine2,
    profile.phone ? `Mobil: ${profile.phone}` : '',
    `E-Mail: ${profile.email}`,
    `Website: ${profile.website}`,
  ].filter((line, index, list) => line || list[index - 1]).join('\n');
}

async function copySignatureToClipboard(html, text) {
  if (navigator.clipboard?.write && window.ClipboardItem) {
    await navigator.clipboard.write([
      new window.ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      }),
    ]);
    return 'html';
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return 'text';
  }

  throw new Error('Zwischenablage wird von diesem Browser nicht unterstützt.');
}

const inputStyle = {
  width: '100%',
  minHeight: '40px',
  border: '1px solid #CBD5E1',
  borderRadius: '8px',
  padding: '9px 11px',
  color: '#122A5C',
  fontFamily: 'Manrope, sans-serif',
  fontSize: '13px',
  fontWeight: 700,
  outline: 'none',
  background: '#FFFFFF',
};

export default function EmailSignaturePage({ apiBase = '/api', session }) {
  const [payload, setPayload] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '' });
  const [platform, setPlatform] = useState('windows');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');

  const accessToken = session?.access_token || '';
  const profile = payload?.profile || EMPTY_PROFILE;

  const authHeaders = useMemo(() => (
    accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
  ), [accessToken]);

  const effectiveProfile = useMemo(() => ({
    ...EMPTY_PROFILE,
    ...profile,
    name: cleanEditableValue(form.name || profile.name || ''),
    phone: cleanEditableValue(form.phone, 80),
    position: FIXED_POSITION,
  }), [form.name, form.phone, profile]);

  const previewLogoSrc = '/enpower-logo-transparent.png';
  const iosLogoSrc = typeof window !== 'undefined'
    ? `${window.location.origin}/enpower-logo-transparent.png`
    : '/enpower-logo-transparent.png';

  const previewHtml = useMemo(() => (
    buildSignatureHtml(effectiveProfile, previewLogoSrc)
  ), [effectiveProfile]);

  const iosHtml = useMemo(() => (
    buildSignatureHtml(effectiveProfile, iosLogoSrc)
  ), [effectiveProfile, iosLogoSrc]);

  const signatureText = useMemo(() => (
    buildSignatureText(effectiveProfile)
  ), [effectiveProfile]);

  const loadSignature = useCallback(async () => {
    if (!accessToken) {
      setError('Keine aktive Anmeldung gefunden.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${apiBase}/contracts/usage?op=email-signature`, {
        headers: authHeaders,
      });
      const data = response.data || null;
      setPayload(data);
      setForm({
        name: data?.profile?.name || '',
        phone: data?.profile?.phone || '',
      });
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail || requestError?.response?.data?.error || requestError.message;
      setError(detail || 'Signatur konnte nicht geladen werden.');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken, apiBase, authHeaders]);

  useEffect(() => {
    loadSignature();
  }, [loadSignature]);

  const profileRows = useMemo(() => ([
    { label: 'Position', value: FIXED_POSITION },
    { label: 'E-Mail', value: getValueLabel(effectiveProfile.email) },
    { label: 'Firma', value: getValueLabel(effectiveProfile.company) },
    { label: 'Website', value: getValueLabel(effectiveProfile.website) },
  ]), [effectiveProfile]);

  const handleWindowsDownload = useCallback(async () => {
    if (!accessToken) {
      toast.error('Keine aktive Anmeldung gefunden.');
      return;
    }

    setWorking(true);
    try {
      const response = await axios.post(`${apiBase}/contracts/usage`, {
        action: 'email-signature-zip',
        name: effectiveProfile.name,
        phone: effectiveProfile.phone,
      }, {
        headers: authHeaders,
        responseType: 'arraybuffer',
      });
      const blob = new Blob([response.data], { type: 'application/zip' });
      saveBlob(blob, 'ENPOWER-Outlook-Signatur.zip');
      toast.success('Windows-Signatur wurde heruntergeladen.');
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail || requestError.message;
      toast.error(detail || 'Download fehlgeschlagen.');
    } finally {
      setWorking(false);
    }
  }, [accessToken, apiBase, authHeaders, effectiveProfile.name, effectiveProfile.phone]);

  const handleClipboardCopy = useCallback(async () => {
    setWorking(true);
    try {
      const mode = await copySignatureToClipboard(iosHtml, signatureText);
      const label = platform === 'new-outlook' ? 'New-Outlook-Signatur' : 'iOS-Signatur';
      toast.success(mode === 'html'
        ? `${label} für ${effectiveProfile.email} wurde kopiert.`
        : `Textsignatur für ${effectiveProfile.email} wurde kopiert.`);
    } catch (copyError) {
      toast.error(copyError.message || 'Kopieren fehlgeschlagen.');
    } finally {
      setWorking(false);
    }
  }, [effectiveProfile.email, iosHtml, platform, signatureText]);

  const handlePrimaryAction = platform === 'windows' ? handleWindowsDownload : handleClipboardCopy;

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', background: '#F1F5F9' }}>
      <div style={{ padding: '24px clamp(18px, 3vw, 36px)', display: 'grid', gap: '18px' }}>
        {/* Kopf "On-Boarding" mit einer Pille "E-Mail Signatur" ohne Funktion
            entfiel am 23.09.2026 - beides steht schon in der Navigation. */}

        <main style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))', gap: '18px', alignItems: 'start' }}>
          <section style={{ background: '#FFFFFF', border: '1px solid #D8E2F0', borderRadius: '10px', overflow: 'hidden' }}>
            <div style={{ padding: '18px 18px 14px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
              <div>
                <h2 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: '18px', color: '#122A5C' }}>Meine Outlook-Signatur</h2>
              </div>
              <button
                type="button"
                onClick={loadSignature}
                disabled={loading}
                aria-label="Neu laden"
                title="Neu laden"
                style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: '10px',
                  border: '1px solid #CBD5E1',
                  background: '#FFFFFF',
                  color: '#122A5C',
                  display: 'grid',
                  placeItems: 'center',
                  cursor: loading ? 'default' : 'pointer',
                }}
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>

            <div style={{ padding: '18px', display: 'grid', gap: '16px' }}>
              <div style={{ display: 'grid', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0F766E', fontFamily: 'Manrope, sans-serif', fontSize: '12px', fontWeight: 700 }}>
                  <ShieldCheck size={16} />
                  E-Mail aus Login: {getValueLabel(effectiveProfile.email, session?.user?.email || 'angemeldet')}
                </div>
                {!payload?.hasProfile && !loading && !error && (
                  <div style={{ border: '1px solid #FED7AA', background: '#FFF7ED', color: '#9A3412', borderRadius: '8px', padding: '10px 12px', fontFamily: 'Manrope, sans-serif', fontSize: '12px', lineHeight: 1.5 }}>
                    Kein eigener profiles-Datensatz gefunden. Die Signatur nutzt die Session-E-Mail und EN:POWER-Standarddaten.
                  </div>
                )}
                {error && (
                  <div style={{ border: '1px solid #FECACA', background: '#FEF2F2', color: '#991B1B', borderRadius: '8px', padding: '10px 12px', fontFamily: 'Manrope, sans-serif', fontSize: '12px', lineHeight: 1.5 }}>
                    {error}
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gap: '10px' }}>
                <label style={{ display: 'grid', gap: '6px', color: '#64748B', fontFamily: 'Manrope, sans-serif', fontSize: '12px', fontWeight: 700 }}>
                  Name
                  <input
                    type="text"
                    value={form.name}
                    onChange={(event) => setForm((current) => ({ ...current, name: cleanEditableValue(event.target.value) }))}
                    style={inputStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: '6px', color: '#64748B', fontFamily: 'Manrope, sans-serif', fontSize: '12px', fontWeight: 700 }}>
                  Telefon
                  <input
                    type="tel"
                    placeholder="+49 ..."
                    value={form.phone}
                    onChange={(event) => setForm((current) => ({ ...current, phone: cleanEditableValue(event.target.value, 80) }))}
                    style={inputStyle}
                  />
                </label>
              </div>

              <div style={{ border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden' }}>
                {profileRows.map((row) => (
                  <div
                    key={row.label}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '112px 1fr',
                      gap: '12px',
                      padding: '10px 12px',
                      borderBottom: row.label === profileRows[profileRows.length - 1].label ? 0 : '1px solid #E2E8F0',
                      fontFamily: 'Manrope, sans-serif',
                      fontSize: '13px',
                    }}
                  >
                    <span style={{ color: '#64748B', fontWeight: 700 }}>{row.label}</span>
                    <span style={{ color: '#122A5C', fontWeight: 700, wordBreak: 'break-word' }}>{row.value}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))', gap: '8px' }}>
                {[
                  { key: 'windows', label: 'Windows ZIP', icon: Monitor },
                  { key: 'new-outlook', label: 'New Outlook', icon: Mail },
                  { key: 'ios', label: 'iOS', icon: Smartphone },
                ].map((item) => {
                  const Icon = item.icon;
                  const active = platform === item.key;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setPlatform(item.key)}
                      style={{
                        border: `1px solid ${active ? '#122A5C' : '#CBD5E1'}`,
                        borderRadius: '8px',
                        background: active ? '#EEF4FF' : '#FFFFFF',
                        color: '#122A5C',
                        minHeight: '40px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontFamily: 'Manrope, sans-serif',
                        fontSize: '13px',
                        fontWeight: 900,
                        cursor: 'pointer',
                      }}
                    >
                      <Icon size={16} />
                      {item.label}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={handlePrimaryAction}
                disabled={loading || working || !payload || !effectiveProfile.name.trim()}
                style={{
                  border: 0,
                  borderRadius: '8px',
                  background: loading || working || !payload || !effectiveProfile.name.trim() ? '#94A3B8' : '#122A5C',
                  color: '#FFFFFF',
                  minHeight: '44px',
                  padding: '10px 16px',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 900,
                  fontSize: '14px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '9px',
                  cursor: loading || working || !payload || !effectiveProfile.name.trim() ? 'default' : 'pointer',
                }}
              >
                {platform === 'windows' ? <Download size={17} /> : <Copy size={17} />}
                {working
                  ? 'Wird vorbereitet...'
                  : platform === 'windows'
                    ? 'Windows-ZIP herunterladen'
                    : platform === 'new-outlook'
                      ? 'Für New Outlook kopieren'
                      : 'Für Outlook iOS kopieren'}
              </button>
              {platform === 'windows' && (
                <div style={{ border: '1px solid #D8E2F0', borderRadius: '8px', background: '#F8FAFC', padding: '10px 12px', color: '#475569', fontFamily: 'Manrope, sans-serif', fontSize: '12px', lineHeight: 1.5 }}>
                  Das ZIP enthält den Installer für klassisches Outlook und zusätzlich eine New-Outlook-Hilfe. Die Zieladresse bleibt {effectiveProfile.email}.
                </div>
              )}
              {platform === 'new-outlook' && (
                <div style={{ border: '1px solid #D8E2F0', borderRadius: '8px', background: '#F8FAFC', padding: '10px 12px', color: '#475569', fontFamily: 'Manrope, sans-serif', fontSize: '12px', lineHeight: 1.5 }}>
                  New Outlook nutzt keine klassische lokale Signaturdatei. Die Signatur wird formatiert kopiert und dann bei Konto {effectiveProfile.email} in Outlook eingefügt.
                </div>
              )}
              {platform === 'ios' && (
                <div style={{ border: '1px solid #D8E2F0', borderRadius: '8px', background: '#F8FAFC', padding: '10px 12px', color: '#475569', fontFamily: 'Manrope, sans-serif', fontSize: '12px', lineHeight: 1.5 }}>
                  Outlook iOS erlaubt keine automatische Kontozuweisung aus einer Datei. Aktiviere in Outlook iOS die Signatur pro Konto und füge sie beim Konto {effectiveProfile.email} ein.
                </div>
              )}
            </div>
          </section>

          <section style={{ background: '#FFFFFF', border: '1px solid #D8E2F0', borderRadius: '10px', overflow: 'hidden', minHeight: '520px' }}>
            <div style={{ padding: '16px 18px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <UserRound size={18} color="#122A5C" />
              <div>
                <div style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', color: '#718096', fontWeight: 700 }}>Vorschau</div>
                <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: '18px', color: '#122A5C' }}>{getValueLabel(effectiveProfile.name, 'Meine Signatur')}</h3>
              </div>
            </div>
            <div style={{ padding: '24px', background: '#FFFFFF' }}>
              {loading ? (
                <div style={{ height: '360px', display: 'grid', placeItems: 'center', color: '#64748B', fontFamily: 'Manrope, sans-serif', fontWeight: 700 }}>
                  Signatur wird geladen...
                </div>
              ) : previewHtml ? (
                <iframe
                  title="ENPOWER Signatur Vorschau"
                  srcDoc={previewHtml}
                  style={{ width: '100%', minHeight: '420px', border: 0, background: '#FFFFFF' }}
                />
              ) : (
                <div style={{ height: '360px', display: 'grid', placeItems: 'center', color: '#991B1B', fontFamily: 'Manrope, sans-serif', fontWeight: 700 }}>
                  Keine Vorschau verfügbar.
                </div>
              )}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
